// Solo se anima lo que comunica algo: cuando una métrica cambia, ver el número
// recorrer la distancia deja claro que el sistema recalculó. Con movimiento
// reducido activado en el sistema operativo, los valores saltan directo al final.

import { useEffect, useMemo, useRef, useState } from 'react';

const DURACION = 650;

// Con la pestaña en segundo plano el navegador deja de entregar cuadros. Animar ahí
// dejaría el número viejo clavado hasta volver a la pestaña.
function sinAnimacion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    || document.visibilityState === 'hidden';
}

const suavizar = (t: number) => 1 - (1 - t) ** 3;

function animar(alAvanzar: (progreso: number) => void, alTerminar: () => void): () => void {
  const inicio = performance.now();
  let cuadro = 0;
  const paso = (ahora: number) => {
    const t = Math.min(1, (ahora - inicio) / DURACION);
    alAvanzar(suavizar(t));
    if (t < 1) cuadro = requestAnimationFrame(paso);
  };
  cuadro = requestAnimationFrame(paso);
  // Si por lo que sea no llegan cuadros, el valor igual termina donde tiene que terminar.
  const red = window.setTimeout(alTerminar, DURACION + 250);
  return () => {
    cancelAnimationFrame(cuadro);
    window.clearTimeout(red);
  };
}

export function useNumeroAnimado(destino: number): number {
  const final = Number.isFinite(destino) ? destino : 0;
  const [valor, setValor] = useState(final);
  const anterior = useRef(final);

  useEffect(() => {
    const desde = anterior.current;
    anterior.current = final;
    if (desde === final) return undefined;
    if (sinAnimacion()) {
      setValor(final);
      return undefined;
    }
    return animar(
      (s) => { setValor(desde + (final - desde) * s); },
      () => { setValor(final); },
    );
  }, [final]);

  return valor;
}

// Si cambia la cantidad de puntos no se interpola: el eje cambió y la transición no
// significaría nada.
export function useSerieAnimada(serie: readonly number[]): readonly number[] {
  // Se compara por contenido: las pantallas arman el arreglo de nuevo en cada
  // render y, comparado por referencia, la animación no terminaría nunca.
  const clave = serie.join('|');
  const destino = useMemo(() => serie, [clave]);
  const [valores, setValores] = useState(destino);
  const anterior = useRef(destino);

  useEffect(() => {
    const desde = anterior.current;
    anterior.current = destino;
    if (desde === destino) return undefined;
    if (desde.length !== destino.length || sinAnimacion()) {
      setValores(destino);
      return undefined;
    }
    return animar(
      (s) => { setValores(destino.map((v, i) => (desde[i] ?? v) + (v - (desde[i] ?? v)) * s)); },
      () => { setValores(destino); },
    );
  }, [destino]);

  return valores;
}

// Toma un valor ya formateado ("$900.000", "9,7%", "28") y lo reformatea mientras se
// anima, conservando prefijo y sufijo. Cada pantalla decide cómo mostrar su número.
export function useValorAnimado(valor: string | number): string {
  const texto = String(valor);
  const crudo = /-?[\d.,]+/.exec(texto)?.[0] ?? null;
  // En español el punto separa miles y la coma marca decimales.
  const numero = crudo ? Number(crudo.replace(/\./g, '').replace(',', '.')) : Number.NaN;
  const animable = crudo !== null && Number.isFinite(numero);
  const decimales = crudo?.split(',')[1]?.length ?? 0;
  const animado = useNumeroAnimado(animable ? numero : 0);

  if (!animable) return texto;
  const partido = animado.toLocaleString('es-AR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
  return texto.replace(crudo, partido);
}
